import { useState, useRef, useEffect } from 'react'
import { cn } from '@/lib/utils'

/**
 * SlidingPillToggle Component
 * Single-select toggle with animated sliding pill indicator.
 * Similar to the BillingToggle component.
 */

export interface SlidingPillOption<T extends string> {
  value: T
  label: string
  icon?: React.ReactNode
}

export interface SlidingPillToggleProps<T extends string> {
  /** Available options */
  options: SlidingPillOption<T>[]
  /** Currently selected value */
  value: T
  /** Change handler */
  onChange: (value: T) => void
  /** Whether to stretch to full width */
  fullWidth?: boolean
  /** Additional class names */
  className?: string
}

export function SlidingPillToggle<T extends string>({
  options,
  value,
  onChange,
  fullWidth = false,
  className,
}: SlidingPillToggleProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRefs = useRef<Map<T, HTMLButtonElement>>(new Map())
  const [pillStyle, setPillStyle] = useState({ left: 0, width: 0 })

  // Update pill position when value changes
  useEffect(() => {
    const container = containerRef.current
    const button = buttonRefs.current.get(value)

    if (container && button) {
      const containerRect = container.getBoundingClientRect()
      const buttonRect = button.getBoundingClientRect()
      setPillStyle({
        left: buttonRect.left - containerRect.left,
        width: buttonRect.width,
      })
    } else {
      // No matching option — hide the pill
      setPillStyle(prev => ({ left: prev.left, width: 0 }))
    }
  }, [value, options])

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative p-0.5 bg-gray-100 rounded-full',
        fullWidth ? 'flex w-full' : 'inline-flex',
        className
      )}
    >
      {/* Sliding Pill Indicator */}
      <div
        className={cn(
          'absolute top-0.5 bottom-0.5 bg-white rounded-full shadow-sm transition-all duration-300 ease-out',
          pillStyle.width === 0 && 'opacity-0',
        )}
        style={{
          left: pillStyle.left,
          width: pillStyle.width,
        }}
      />

      {/* Options */}
      {options.map((option) => (
        <button
          key={option.value}
          ref={(el) => {
            if (el) buttonRefs.current.set(option.value, el)
          }}
          type="button"
          onClick={() => onChange(option.value)}
          className={cn(
            'relative z-10 inline-flex items-center justify-center gap-1 px-3 py-1 rounded-full',
            'text-xs font-medium transition-colors duration-200 whitespace-nowrap',
            fullWidth && 'flex-1',
            value === option.value
              ? 'text-gray-900 font-semibold'
              : 'text-gray-500 hover:text-gray-700'
          )}
          aria-pressed={value === option.value}
        >
          {option.icon}
          {option.label}
        </button>
      ))}
    </div>
  )
}

export default SlidingPillToggle
